import { StandardResponse } from "./types/types";
import stringify from "fast-safe-stringify";
import fs from "fs";
import path from "path";
import os from "os";
import { Notification } from "electron";
import isNumber from "lodash/isNumber";

/**
 * Retries an operation a specified number of times
 *
 * @param operation the operation to retry
 * @param maxRetries the maximum number of retries
 * @param delay the time to wait before retrying (in milliseconds)
 * @returns the result of "operation", or a thrown error with all error messages
 */
export const retry = async (operation: Function, maxRetries: number = 3, delay: number = 250) => {
	// The number of attempts
	let attempts = 0;
	// A concatenation of all encountered error messages
	let errorMessages = "";

	// Try to execute "operation" upto the number of "maxRetries"
	while (attempts < maxRetries) {
		try {
			// Attempt the operation
			return await operation();
		} catch (error) {
			// Increase the attempt counter
			attempts++;
			// Concatenate the error message
			const errorMessage = (error as any)?.message;
			if (!!errorMessage && "" !== errorMessage.trim()) {
				errorMessages = `${errorMessages}${errorMessage};`;
			}
			// If max retries has been reached, fail
			if (attempts >= maxRetries) {
				throw new Error(`Operation failed after ${maxRetries} attempts: ${errorMessages}`);
			}
			// Wait before retrying
			await new Promise((resolve) => setTimeout(resolve, delay));
		}
	}
};

// https://stackoverflow.com/questions/7616461/generate-a-hash-from-string-in-javascript
/**
 * Returns a hash value for a string
 * @param {String} str - input string
 * @param {Boolean} asString - output as a string
 * @param {Number} seed
 */
export const hashFn = (str: string, asString = true, seed: number | undefined = undefined) => {
	/*jshint bitwise:false */
	let hval = seed === undefined ? 0x811c9dc5 : seed;

	for (let i = 0; i < str.length; i++) {
		hval = hval ^ str.charCodeAt(i);
		hval = hval + ((hval << 1) + (hval << 4) + (hval << 7) + (hval << 8) + (hval << 24));
	}
	if (asString) {
		return `0000000000000000${(hval >>> 0).toString(16)}`.substr(-16);
	}
	return hval >>> 0;
};

// If JSON.stringify fails, we'll use the safer, but slightly slower option above.
export const safeStringify = (obj: any, replacer = null, spaces = 2) => {
	if (obj === null || typeof obj === "undefined") return "";
	try {
		return JSON.stringify(obj, replacer, spaces);
	} catch (e) {
		return stringify(obj, replacer ?? undefined, spaces);
	}
};

export const deleteFolderRecursive = (_path: string) => {
	// https://geedew.com/remove-a-directory-that-is-not-empty-in-nodejs/
	if (fs.existsSync(_path)) {
		fs.readdirSync(_path).forEach((file) => {
			const curPath = `${_path}/${file}`;
			if (fs.lstatSync(curPath).isDirectory()) {
				// recurse
				deleteFolderRecursive(curPath);
			} else {
				// delete file
				fs.unlinkSync(curPath);
			}
		});
		fs.rmdirSync(_path);
	}
};

/**
 * Function that returns an accessDenied error callback or string including the passed in method name
 * @param {object} methodName
 * @param {*} cb
 */
export const accessDenied = (methodName: string, cb: Function | null = null) => {
	const errorText = `api_access_denied: Access to API method ${methodName} denied.`;
	if (cb) {
		return cb({ error: "api_access_denied", message: errorText });
	}
	return errorText;
};

/**
 * @returns Parse a string into a URL, returning null if an exception occurs
 */
export const getURL = (str: string) => {
	try {
		return new URL(str);
	} catch (e) {
		return null;
	}
};

/**
 * @returns Boolean whether the URL is valid
 */
export const isValidURL = (str: string) => !!getURL(str);

/**
 * Checks a url's protocol against an allowed list.
 * Allows for case insensitivity and omission of trailing ':'
 * @param urlStr
 * @param allowedProtocols array of strings
 * @returns Boolean whether the URL has a protocol that matches one of the allowed protocols
 *
 */
export const isValidProtocol = (urlStr: string, allowedProtocols: string[]) => {
	const navProt = getURL(urlStr)?.protocol?.toLowerCase()?.replace(":", "");
	return navProt !== null && allowedProtocols.some((prot) => prot.toLowerCase().replace(":", "") === navProt);
};

/**
 * @return Boolean whether the URL has a %00 (encoded zero character) in the pathname or not.
 * Having %00 in the pathname is an issue, since Windows spawns explorer instead of the default Browser.
 * This is a workaround of that odd behavior.
 */
export const isNullPresentInUrl = (url: string) => {
	try {
		// %00 is still valid after ? or #, and those are not spawning explorer.exe anyway.
		const parsed = new URL(url);
		return parsed.pathname.indexOf("%00") != -1;
	} catch (e) {
		return true;
	}
};

/**
 * Check's a url's host or origin against an allowed list.  Allows for case insensitivity. Allows for matching subdomains
 * by checking that the hostname of urlstr ends with any entry in allowedHosts.
 * Also allows for the complete origin (protocol + hostname) to match exactly.
 * @param urlStr
 * @param allowedHosts array of strings
 * @returns Boolean whether the URL has an allowed host.
 */
export const isValidHost = (urlStr: string, allowedHosts: string[]) => {
	const navHost = getURL(urlStr)?.host?.toLowerCase();
	const navOrigin = getURL(urlStr)?.origin?.toLowerCase();
	return (
		navHost !== null &&
		allowedHosts.some((host) => navOrigin === host.toLowerCase() || navHost?.endsWith(host.toLowerCase()))
	);
};

/**
 * Compares two urls for whether they are same-domain
 * @param urlStr
 * @param urlStrOther
 * @returns
 */
export const isSameDomain = (urlStr: string, urlStrOther: string) =>
	getURL(urlStr)?.host?.toLowerCase() === getURL(urlStrOther)?.host?.toLowerCase();

export const getFilenameFromURL = (url: string) => url.substring(url.lastIndexOf("/") + 1);

/**
 * Returns true if FEA was launched from the seed or from a protocol handler registered by the seed.
 */
export const isDevLaunch = (): boolean => !!process.env.ELECTRON_DEV || process.execPath.endsWith("electron.exe");

/**
 * Returns true if FEA was launched in dev mode (e.g. from the seed)
 */
export const isDevMode = (): boolean => !!process.env.ELECTRON_DEV;

/**
 * Compresses or expands an object in the same manner as Array.flatMap();
 *
 * Requires a Record<string,any>. Do not pass empty or primitive objects.
 *
 * Usage:
 * objectFlatMap(object, (key, value) => {
		// Remove key/value pair
		if(key === "goodbye") return [];
		// Add key/value pair
		if(key === "add") return [[key,value],[key2,value2]]
		// Retain/replace key/value pair
		return [[key,value]];
	});
 */
export const objectFlatMap = (
	obj: Record<string, any>,
	func: (key: string, value: any) => [key: string, value: any][]
) => Object.fromEntries(Object.entries(obj).flatMap(([k, v]) => func(k, v)));

/**
 * Get an object's value when the casing of the key can change, such as response headers of webservers
 * that sometimes lower-case all keys
 * @param obj
 * @param key
 * @returns value for key
 */
export const objectGetValueCaseInsensitive = (obj: Record<string, any>, key: string): any =>
	Object.entries(obj).find(([k]) => k.toLowerCase() === key.toLowerCase())?.[1];

/*
 * The same as path.join except that it prevents filesystem hacks by ensuring that the user provided
 * path does not break out of the root folder. Any attempt to break out of the root folder causes
 * an error to be returned.
 *
 * @param rootFolder - The folder to join the path to (e.g. "./www/blah" or "./" or "/www/var")
 * @param userProvidedPath - The path or filename to append
 * @returns - Response tuple with error or valid string
 */
export const pathJoinSecureRoot = (rootFolder: string, userProvidedPath: string): StandardResponse<string> => {
	// https://nodejs.org/en/knowledge/file-system/security/introduction/#poison-null-bytes
	if (userProvidedPath.indexOf("\0") !== -1) {
		return { err: `pathJoinSecureRoot(): Detected illegal \\0 in userProvidedPath "${userProvidedPath}"` };
	}

	// Convert to this operating system's format so that slashes can be matched
	const osRootFolder = path.join(rootFolder);

	const joinedPath = path.join(osRootFolder, userProvidedPath);

	//https://nodejs.org/en/knowledge/file-system/security/introduction/#preventing-directory-traversal
	if (joinedPath.indexOf(osRootFolder) !== 0) {
		return {
			err: `pathJoinSecureRoot(): Security violation: asset "${userProvidedPath}" has broken out of root folder "${osRootFolder}"`,
		};
	}
	return { data: joinedPath };
};

export function isWindows(): boolean {
	return os.type().toUpperCase().includes("WINDOWS");
}

/**
 * Helper method to call function surrounded with a try-catch.  If an errorMessage is supplied,
 * a notification will be shown.
 * @param fn: A nullary function to call that can throw an exception
 * @param logger: winston logger to log errorMessage and the error itself
 */
export function tryCatchNotify<T>(fn: () => T, errorMessage?: string, logger?: { error: Function }): T | undefined {
	try {
		return fn();
	} catch (error) {
		if (errorMessage) {
			logger?.error?.(errorMessage);
			logger?.error?.(error);
			new Notification({ body: errorMessage }).show();
		} else {
			logger?.error?.(error);
		}
		return undefined;
	}
}

/**
 * When using Promise.AllSettled, typescript doesn't know which promises are fulfilled.
 * This generic predicate preserves the template type while doing a type guard check.
 *
 * Can also use:
 *
 * `const isRejected = (input: PromiseSettledResult<unknown>): input is PromiseRejectedResult => input.status === 'rejected'`
 * @example const resultStatuses = await Promise.allSettled(promises);
			const results = resultStatuses.filter(isFulfilled).map(p => p.value)
 * @param input
 * @returns boolean indicating if a promise was fulfilled (true) or rejected (false)
 */
export const isFulfilled = <T>(input: PromiseSettledResult<T>): input is PromiseFulfilledResult<T> =>
	input.status === "fulfilled";

/**
 * Templated generator for a promise and its resolve and reject functions.
 *
 * https://stackoverflow.com/a/48592904
 */
export const deferred = <T>() => {
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	let resolve = (_value: T) => {};
	let reject = () => {};
	const promise = new Promise<T>((res, rej) => {
		resolve = res;
		reject = rej;
	});
	return { promise, resolve, reject };
};

/**
 * Converts a value to a number if possible, otherwise returns defaultValue.
 *
 * @param value value to convert to number
 * @returns the number value, or defaultValue if conversion is not possible
 * @throws if value is a string that cannot be parsed to a number
 */
export const asNumber = (value: any): number => {
	if (typeof value === "number") {
		return value;
	}
	// The empty string is parsed as a 0, so we exclude it here
	if (typeof value === "string" && "" !== value) {
		const parsed = Number(value);
		if (!isNaN(parsed)) {
			return parsed;
		}
	}
	throw new Error(`Value "${value}" cannot be converted to a number`);
};

/**
 * Converts a value to an integer if possible, otherwise returns defaultValue.
 *
 * @param value value to convert to integer
 * @returns the integer value, or defaultValue if conversion is not possible
 * @throws if value is a string that cannot be parsed to a number
 */
export const asInteger = (value: any): number => Math.round(asNumber(value));

/**
 * Ensures that all values in an Electron.Rectangle are integers.
 *
 * @param bounds An Electron.Rectangle object
 * @returns a new Electron.Rectangle object with all values converted to integers
 */
export const ensureBoundsHaveIntegerValues = (bounds: Electron.Rectangle): Electron.Rectangle => {
	return {
		x: asInteger(bounds.x),
		y: asInteger(bounds.y),
		width: asInteger(bounds.width),
		height: asInteger(bounds.height),
	};
};

/**
 * Determines if all members of an Electron.Rectangle are present and are numbers.
 *
 * @param bounds An Electron.Rectangle object
 * @returns true if all members of bounds are numbers, false otherwise
 */
export const validateBoundsCanBeWellFormed = (bounds: Electron.Rectangle): boolean =>
	!!bounds && isNumber(bounds.x) && isNumber(bounds.y) && isNumber(bounds.width) && isNumber(bounds.height);
